import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminOrders from './AdminOrders'

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalCategories: 0,
    featuredProducts: 0,
    outOfStock: 0
  });

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // États pour le CRUD
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [productForm, setProductForm] = useState({
    name: '',
    short_description: '',
    ingredients: '',
    how_to_use: '',
    size: '',
    price: '',
    category_id: '',
    image: null,
    featured: 0,
    in_stock: 1
  });

  // État pour la prévisualisation d'image
  const [imagePreview, setImagePreview] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    loadProductsAndStats();
  }, []);

  const loadProductsAndStats = async () => {
    try {
      console.log('🔄 Chargement des données...');

      let loadedProducts = [];

      // Charger les produits depuis l'API admin
      const productsResponse = await fetch('https://d4unaturally.com/api/admin-products.php', {
        credentials: 'include'
      });

      if (productsResponse.ok) {
        const productsData = await productsResponse.json();
        if (productsData.success) {
          console.log('🎯 Produits chargés:', productsData.data.length);
          loadedProducts = productsData.data;
          setProducts(productsData.data);
        }
      }

      // Charger les catégories
      const categoriesResponse = await fetch('https://d4unaturally.com/api/categories-with-products.php');
      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        if (Array.isArray(categoriesData)) {
          setCategories(categoriesData);
          calculateStats(loadedProducts, categoriesData);
        }
      }
    } catch (error) {
      console.error('💥 Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (productsList, categoriesList) => {
    const stats = {
      totalProducts: productsList.length,
      totalCategories: categoriesList.length,
      featuredProducts: productsList.filter(p => p.featured === 1).length,
      outOfStock: productsList.filter(p => p.in_stock === 0).length
    };
    setStats(stats);
  };

  // ==================== FONCTIONS DE GESTION D'IMAGES ====================

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner un fichier image valide');
        return;
      }

      // Vérifier la taille du fichier (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('La taille de l\'image ne doit pas dépasser 5MB');
        return;
      }

      setProductForm({
        ...productForm,
        image: file
      });

      // Créer une URL pour la prévisualisation
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const createProductFormData = (productData, isEditing = false) => {
    const formData = new FormData();

    // Ajouter tous les champs du formulaire
    Object.keys(productData).forEach(key => {
      if (key === 'image' && productData[key] instanceof File) {
        formData.append('image', productData[key]);
      } else if (key !== 'image' || (key === 'image' && productData[key])) {
        formData.append(key, productData[key]);
      }
    });

    // Pour l'édition, on indique si on veut garder l'ancienne image
    if (isEditing && !productData.image) {
      formData.append('keep_existing_image', '1');
    }

    return formData;
  };

  // ==================== CRUD FUNCTIONS ====================

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || '',
      short_description: product.short_description || '',
      ingredients: product.ingredients || '',
      how_to_use: product.how_to_use || '',
      size: product.size || '',
      price: product.price || '',
      category_id: product.category_id || '',
      image: null,
      featured: product.featured || 0,
      in_stock: product.in_stock || 1
    });
    // Si le produit a une image, on la met en prévisualisation
    setImagePreview(product.image || '');
    setShowProductForm(true);
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const productData = {
        ...productForm,
        price: parseFloat(productForm.price),
        featured: productForm.featured ? 1 : 0,
        in_stock: productForm.in_stock ? 1 : 0
      };

      const formData = createProductFormData(productData, false);

      const response = await fetch('https://d4unaturally.com/api/admin-products.php', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        alert('✅ Produit ajouté avec succès!');
        resetProductForm();
        setShowProductForm(false);
        loadProductsAndStats();
      } else {
        alert(`❌ Erreur: ${result.error}`);
      }
    } catch (error) {
      console.error('💥 Erreur ajout produit:', error);
      alert('❌ Erreur lors de l\'ajout du produit');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const productData = {
        ...productForm,
        price: parseFloat(productForm.price),
        featured: productForm.featured ? 1 : 0,
        in_stock: productForm.in_stock ? 1 : 0
      };

      const formData = createProductFormData(productData, true);

      const response = await fetch(`https://d4unaturally.com/api/admin-products.php?id=${editingProduct.id}`, {
        method: 'PUT',
        credentials: 'include',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        alert('✅ Produit modifié avec succès!');
        resetProductForm();
        setShowProductForm(false);
        loadProductsAndStats();
      } else {
        alert(`❌ Erreur: ${result.error}`);
      }
    } catch (error) {
      console.error('💥 Erreur modification produit:', error);
      alert('❌ Erreur lors de la modification du produit');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteProduct = async (product) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${product.name}" ? Cette action est irréversible.`)) {
      try {
        const response = await fetch(`https://d4unaturally.com/api/admin-products.php?id=${product.id}`, {
          method: 'DELETE',
          credentials: 'include'
        });

        const result = await response.json();

        if (result.success) {
          alert('✅ Produit supprimé avec succès!');
          loadProductsAndStats();
        } else {
          alert(`❌ Erreur: ${result.error}`);
        }
      } catch (error) {
        console.error('💥 Erreur suppression produit:', error);
        alert('❌ Erreur lors de la suppression du produit');
      }
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      short_description: '',
      ingredients: '',
      how_to_use: '',
      size: '',
      price: '',
      category_id: '',
      image: null,
      featured: 0,
      in_stock: 1
    });
    setImagePreview('');
    setEditingProduct(null);
  };

  const handleLogout = async () => {
    try {
      await fetch('https://d4unaturally.com/api/logout.php', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Erreur déconnexion:', error);
    } finally {
      localStorage.removeItem('user');
      navigate('/admin/login');
    }
  };

  // ==================== RENDER ====================

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#d0b125] mx-auto mb-4"></div>
          <div className="text-xl font-light text-gray-700">Chargement des données...</div>
          <div className="text-sm text-gray-500 mt-2">Veuillez patienter</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-serif font-light text-gray-900">Tableau de Bord Administrateur</h1>
            <p className="text-gray-600 font-light">Gestion des produits D4U Naturally</p>
          </div>
          <div className="flex items-center space-x-6">
            <span className="text-gray-700 font-medium">
              Connecté en tant que <strong className="text-[#d0b125]">{user?.name || user?.email}</strong>
            </span>
            <button
              onClick={handleLogout}
              className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 rounded-full hover:from-red-700 hover:to-red-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-medium"
            >
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Déconnexion
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Statistiques */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-blue-500 hover:shadow-xl transition-all duration-300 hover:translate-y-1">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-600">Produits Total</h3>
                <p className="text-3xl font-bold text-gray-900">{stats.totalProducts}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-500 hover:shadow-xl transition-all duration-300 hover:translate-y-1">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-600">Catégories</h3>
                <p className="text-3xl font-bold text-gray-900">{stats.totalCategories}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-yellow-500 hover:shadow-xl transition-all duration-300 hover:translate-y-1">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-600">Produits Vedette</h3>
                <p className="text-3xl font-bold text-gray-900">{stats.featuredProducts}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-red-500 hover:shadow-xl transition-all duration-300 hover:translate-y-1">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-600">Rupture de Stock</h3>
                <p className="text-3xl font-bold text-gray-900">{stats.outOfStock}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Liste des produits */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="px-8 py-6 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
            <div>
              <h2 className="text-2xl font-serif font-light text-gray-900">Gestion des Produits</h2>
              <p className="text-sm text-gray-500 font-light">
                {products.length} produit(s) • {stats.featuredProducts} en vedette • {stats.outOfStock} en rupture
              </p>
            </div>
            <button
              onClick={() => {
                resetProductForm();
                setShowProductForm(true);
              }}
              className="bg-gradient-to-r from-[#d0b125] to-amber-500 text-white px-6 py-3 rounded-full hover:from-amber-500 hover:to-amber-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-medium flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Ajouter un produit
            </button>
          </div>

          {products.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <p className="text-gray-500 text-lg">Aucun produit trouvé.</p>
              <p className="text-gray-400 mt-2">Commencez par ajouter votre premier produit</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-8 py-4 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">
                      Produit
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">
                      Catégorie
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">
                      Prix
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">
                      Taille
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product) => {
                    const productCategory = categories.find(cat => cat.id === product.category_id);

                    return (
                      <tr key={product.id} className={product.in_stock === 0 ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50 transition-colors duration-200'}>
                        <td className="px-8 py-5">
                          <div className="flex items-center">
                            {product.image && (
                              <img
                                className="h-14 w-14 rounded-xl object-cover mr-4 border border-gray-200 shadow-sm"
                                src={product.image}
                                alt={product.name}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            <div>
                              <div className="text-base font-medium text-gray-900">{product.name}</div>
                              <div className="text-sm text-gray-500 line-clamp-2 max-w-xs mt-1">
                                {product.short_description?.substring(0, 80)}...
                              </div>
                              {product.featured === 1 && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mt-2 border border-yellow-200">
                                  ⭐ Vedette
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          <span className="text-sm text-gray-700 bg-gray-100 px-3 py-1 rounded-full border">
                            {productCategory?.name || 'Non catégorisé'}
                          </span>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          <span className="text-lg font-bold text-[#d0b125]">
                            {product.price} DH
                          </span>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-600">
                          {product.size || 'N/A'}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${product.in_stock === 1
                              ? 'bg-green-100 text-green-800 border border-green-200'
                              : 'bg-red-100 text-red-800 border border-red-200'
                            }`}>
                            {product.in_stock === 1 ? '✅ En stock' : '❌ Rupture'}
                          </span>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap space-x-2">
                          <button
                            onClick={() => handleEditProduct(product)}
                            className="bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium hover:shadow-md transform hover:scale-105 flex items-center"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(product)}
                            className="bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium hover:shadow-md transform hover:scale-105 flex items-center"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Statistiques et gestion produits existantes */}

            {/* Ajouter les commandes en bas */}
            <AdminOrders />
          </div>
        </div>

        {/* Résumé simplifié */}
        
      </div>

      {/* Modal Formulaire Produit */}
      {showProductForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
              <h3 className="text-2xl font-serif font-light text-gray-900">
                {editingProduct ? 'Modifier le Produit' : 'Ajouter un Nouveau Produit'}
              </h3>
              <p className="text-gray-600 text-sm mt-1">
                {editingProduct ? 'Mettez à jour les informations du produit' : 'Remplissez les informations du nouveau produit'}
              </p>
            </div>

            <form onSubmit={editingProduct ? handleUpdateProduct : handleAddProduct} className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du produit *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#d0b125] focus:border-transparent transition duration-200"
                    value={productForm.name}
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prix (DH) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#d0b125] focus:border-transparent transition duration-200"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description courte
                </label>
                <textarea
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#d0b125] focus:border-transparent transition duration-200"
                  value={productForm.short_description}
                  onChange={(e) => setProductForm({ ...productForm, short_description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ingrédients
                  </label>
                  <textarea
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#d0b125] focus:border-transparent transition duration-200"
                    value={productForm.ingredients}
                    onChange={(e) => setProductForm({ ...productForm, ingredients: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mode d'utilisation
                  </label>
                  <textarea
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#d0b125] focus:border-transparent transition duration-200"
                    value={productForm.how_to_use}
                    onChange={(e) => setProductForm({ ...productForm, how_to_use: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Taille
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#d0b125] focus:border-transparent transition duration-200"
                    placeholder="ex: 50ml, 100g"
                    value={productForm.size}
                    onChange={(e) => setProductForm({ ...productForm, size: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Catégorie *
                  </label>
                  <select
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#d0b125] focus:border-transparent transition duration-200"
                    value={productForm.category_id}
                    onChange={(e) => setProductForm({ ...productForm, category_id: e.target.value })}
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name} ({category.gender})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center justify-center space-x-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="featured"
                      className="h-5 w-5 text-[#d0b125] focus:ring-[#d0b125] border-gray-300 rounded"
                      checked={productForm.featured === 1}
                      onChange={(e) => setProductForm({ ...productForm, featured: e.target.checked ? 1 : 0 })}
                    />
                    <label htmlFor="featured" className="ml-2 block text-sm text-gray-700 font-medium">
                      Produit vedette
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="in_stock"
                      className="h-5 w-5 text-[#d0b125] focus:ring-[#d0b125] border-gray-300 rounded"
                      checked={productForm.in_stock === 1}
                      onChange={(e) => setProductForm({ ...productForm, in_stock: e.target.checked ? 1 : 0 })}
                    />
                    <label htmlFor="in_stock" className="ml-2 block text-sm text-gray-700 font-medium">
                      En stock
                    </label>
                  </div>
                </div>
              </div>

              {/* Section Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image du produit
                </label>

                {/* Prévisualisation */}
                {(imagePreview || (editingProduct && editingProduct.image)) && (
                  <div className="mb-4 p-4 border border-gray-200 rounded-xl bg-gray-50">
                    <p className="text-sm text-gray-600 mb-2 font-medium">Prévisualisation:</p>
                    <img
                      src={imagePreview || editingProduct.image}
                      alt="Preview"
                      className="h-40 w-40 object-cover rounded-lg border-2 border-gray-300 shadow-sm"
                    />
                  </div>
                )}

                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-[#d0b125] transition duration-200">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" className="cursor-pointer">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-gray-600 font-medium">Cliquez pour uploader une image</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Formats: JPG, PNG, GIF, WEBP • Max: 5MB
                    </p>
                  </label>
                </div>

                {editingProduct && editingProduct.image && (
                  <p className="text-xs text-amber-600 mt-2 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Laissez vide pour conserver l'image actuelle
                  </p>
                )}
              </div>

              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowProductForm(false);
                    resetProductForm();
                  }}
                  className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition duration-200 border border-gray-300 hover:shadow-md"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-[#d0b125] to-amber-500 rounded-xl hover:from-amber-500 hover:to-amber-600 disabled:opacity-50 transition duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  {formLoading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Enregistrement...
                    </span>
                  ) : (
                    editingProduct ? 'Modifier le Produit' : 'Ajouter le Produit'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;